import { useEffect, useState } from 'react';
import { openTickerChart } from '../tickerChart';
import { OrderTicket } from './OrderTicket';

interface ScreenerStock {
  ticker: string;
  name: string | null;
  price: number | null;
  changePercent: number | null;
  mentions: number | null;
  dollarVolume: number | null;
  activityScore: number;
}

type SortKey = 'ticker' | 'price' | 'change' | 'traded' | 'mentions' | 'activity';
type Filter = 'ALL' | 'GAINERS' | 'LOSERS' | 'BUZZING';

const num = (v: number | null) => (v === null ? -Infinity : v);

function compactDollars(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${(n / 1e3).toFixed(0)}K`;
}

export function ScreenerTable() {
  const [stocks, setStocks] = useState<ScreenerStock[] | null>(null);
  const [error, setError] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>('activity');
  const [filter, setFilter] = useState<Filter>('ALL');
  const [search, setSearch] = useState('');
  const [armed, setArmed] = useState(false); // order buttons only when local execution is enabled
  const [ticket, setTicket] = useState<{
    ticker: string;
    price: number | null;
    action: 'BUY' | 'SELL';
  } | null>(null);

  useEffect(() => {
    fetch('/api/orders/armed')
      .then((r) => (r.ok ? r.json() : { armed: false }))
      .then((d) => setArmed(Boolean(d.armed)))
      .catch(() => setArmed(false));
  }, []);

  useEffect(() => {
    const load = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/api/discover', { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setStocks(data.screener || []);
        setError(false);
      } catch (e) {
        console.error('Error fetching screener:', e);
        setError(true);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const interval = setInterval(load, 300000);
    return () => clearInterval(interval);
  }, []);

  if (error && !stocks) return <div className="empty-state">Screener unavailable</div>;
  if (!stocks) return <div className="loading">Loading...</div>;

  const q = search.trim().toUpperCase();
  const filtered = stocks.filter((s) => {
    if (q && !s.ticker.includes(q) && !(s.name || '').toUpperCase().includes(q)) return false;
    switch (filter) {
      case 'GAINERS':
        return (s.changePercent ?? 0) > 0;
      case 'LOSERS':
        return (s.changePercent ?? 0) < 0;
      case 'BUZZING':
        return (s.mentions ?? 0) > 0;
      default:
        return true;
    }
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'ticker':
        return a.ticker.localeCompare(b.ticker);
      case 'price':
        return num(b.price) - num(a.price);
      case 'change':
        return num(b.changePercent) - num(a.changePercent);
      case 'traded':
        return num(b.dollarVolume) - num(a.dollarVolume);
      case 'mentions':
        return (b.mentions ?? 0) - (a.mentions ?? 0);
      default:
        return b.activityScore - a.activityScore;
    }
  });

  const th = (key: SortKey, label: string) => (
    <th onClick={() => setSortBy(key)} className={sortBy === key ? 'active' : ''}>
      {label} {sortBy === key && '↓'}
    </th>
  );

  return (
    <div className="screener">
      <div className="screener-controls">
        <input
          className="filter-select screener-search"
          placeholder="Search ticker…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          className="filter-select"
        >
          <option value="ALL">All</option>
          <option value="GAINERS">Gainers</option>
          <option value="LOSERS">Losers</option>
          <option value="BUZZING">Reddit Buzz</option>
        </select>
      </div>

      <div className="table-container">
        <table className="screener-table">
          <thead>
            <tr>
              {th('ticker', 'Ticker')}
              {th('price', 'Price')}
              {th('change', 'Chg%')}
              {th('traded', 'Traded')}
              {th('mentions', 'Reddit')}
              {th('activity', 'Activity')}
              {armed && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr
                key={s.ticker}
                className="clickable-row"
                title={`${s.name || s.ticker} — click for chart`}
                onClick={() => openTickerChart(s.ticker)}
              >
                <td className="ticker-cell">
                  <strong>{s.ticker}</strong>
                </td>
                <td>{s.price !== null ? `$${s.price.toFixed(2)}` : '—'}</td>
                <td
                  style={{
                    color:
                      s.changePercent === null
                        ? 'var(--text-dim)'
                        : s.changePercent >= 0
                          ? 'var(--green)'
                          : 'var(--red)',
                    fontWeight: 600,
                  }}
                >
                  {s.changePercent !== null
                    ? `${s.changePercent >= 0 ? '+' : ''}${s.changePercent.toFixed(2)}%`
                    : '—'}
                </td>
                <td>{s.dollarVolume !== null ? compactDollars(s.dollarVolume) : '—'}</td>
                <td>{s.mentions || '—'}</td>
                <td className="score-cell">
                  <div className="score-bar">
                    <div
                      className="score-fill"
                      style={{
                        width: `${s.activityScore}%`,
                        backgroundColor:
                          s.activityScore >= 60
                            ? 'var(--red)'
                            : s.activityScore >= 30
                              ? 'var(--amber)'
                              : 'var(--green)',
                      }}
                    ></div>
                  </div>
                  <span>{s.activityScore}</span>
                </td>
                {armed && (
                  <td>
                    <div className="row-actions">
                      <button
                        className="buy-btn"
                        title={`Buy ${s.ticker}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTicket({ ticker: s.ticker, price: s.price, action: 'BUY' });
                        }}
                      >
                        B
                      </button>
                      <button
                        className="sell-btn"
                        title={`Sell ${s.ticker} (blocked unless you hold it)`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTicket({ ticker: s.ticker, price: s.price, action: 'SELL' });
                        }}
                      >
                        S
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <p>
          {sorted.length} of {stocks.length} tickers · market-wide
        </p>
      </div>

      {ticket && (
        <OrderTicket
          ticker={ticket.ticker}
          action={ticket.action}
          maxQty={1}
          mark={ticket.price}
          onClose={() => setTicket(null)}
        />
      )}
    </div>
  );
}
